import React, { useCallback, useEffect, useRef, useState } from "react"
import { Play } from "lucide-react"

import Tile from "../../components/structure/tile/tile"
import {
	Stage,
	setStageStatus,
	setStageMeta,
	setItemStatus,
	setStageSummary,
	setGroupStatus,
	setSubStatus,
	setSubMeta,
	setSubItemStatus,
} from "../../components/timeline"

import "../../components/timeline/timeline.scss"

/**
 * /design — admin-only sandbox that demos the reusable timeline UI
 * (src/components/timeline) by scripting a fake agent run through it. The
 * timeline rendering + state helpers live in the shared module; everything
 * below is just the demo scheduler that drives them.
 */

/* ------------------------------------------------------------------ */
/* Seed + demo helpers                                                */
/* ------------------------------------------------------------------ */

const SEED = {
	stages: [
		{
			id: "blog-post",
			header: { icon: "file-text", title: "Blog post update", action: { label: "View plan" } },
			trigger: { icon: "zap", label: "Agent Triggered" },
			activity: {
				status: "complete",
				title: "Set visibility alert",
				meta: { steps: "Steps 6 of 6", elapsed: "8m 12s" },
				summary: ["Threshold set < 14%", "2 daily checks", "Summary posted to #brand-team"],
				items: [
					{
						status: "complete",
						content: "Read /docs/model-coverage",
						sub: "1,420 words • 6 sections",
						duration: "1.2s",
					},
					{ status: "complete", content: "Fetch competitor citations (last 7d)", duration: "3.4s" },
					{ status: "complete", content: "Update matrix with Gemini 2.5, Llama 4", duration: "8.1s" },
					{ status: "complete", content: "Draft /briefs/tool-use-benchmarks", duration: "1m 14s" },
					{ status: "complete", content: "Set visibility alert (< 14% • 2 checks)", duration: "6s" },
					{ status: "complete", content: "Post summary to #brand-team", duration: "1.8s" },
				],
			},
		},
		{
			id: "review-draft",
			header: { icon: "list-checks", title: "Review draft", action: { label: "View plan" } },
			trigger: [
				{ icon: "zap", label: "QA Triggered" },
				{ icon: "check", label: "Last review passed", variant: "success" },
				{ icon: "info", label: "Using v2.4 review prompt", variant: "info" },
				{
					block: true,
					phases: [
						{ label: "Awaiting runtime", variant: "pending" },
						{ duration: 2000, label: "Connecting to runtime…", variant: "loading" },
						{ icon: "check", label: "Runtime ready", variant: "success" },
					],
				},
			],
			activity: {
				status: "active",
				title: "Run review checks",
				meta: { steps: "Steps 1 of 3", elapsed: "42s" },
				summary:
					"Markdown linted clean — no warnings raised. 12 external links verified active. Alt text confirmed on all 6 images.",
				items: [
					{
						status: "complete",
						content: "Lint markdown",
						duration: "0.9s",
						summaryAt: "Markdown linted clean — no warnings raised.",
					},
					{
						status: "active",
						content: "Verify external links",
						summaryAt: "Markdown linted clean — no warnings raised. 12 external links verified active.",
					},
					{
						status: "pending",
						content: "Check image alt text",
						summaryAt:
							"Markdown linted clean — no warnings raised. 12 external links verified active. Alt text confirmed on all 6 images.",
					},
				],
			},
		},
		{
			id: "next-process",
			header: { icon: "git-branch", title: "Publish + sync", action: { label: "View plan" } },
			trigger: { icon: "zap", label: "Next process, same as Agent Triggered" },
			activity: {
				status: "pending",
				title: "Run downstream checks",
				meta: { steps: "Steps 0 of 4" },
				summary: ["Published to /blog", "Announced in #editorial"],
				items: [
					{ status: "pending", content: "Validate post schema" },
					{ status: "pending", content: "Cross-link references" },
					{ status: "pending", content: "Publish to /blog" },
					{ status: "pending", content: "Notify #editorial" },
				],
			},
		},
		{
			id: "parallel-eval",
			header: { icon: "code", title: "Quality evaluations", action: { label: "View plan" } },
			trigger: { icon: "zap", label: "Evaluations dispatched" },
			group: {
				status: "pending",
				title: "Run 3 evals in parallel",
				activities: [
					{
						id: "eval-tone",
						status: "pending",
						title: "Eval: tone alignment",
						meta: { steps: "Steps 0 of 3" },
						summary: ["50 examples", "Within 2σ of baseline"],
						items: [
							{ status: "pending", content: "Compile prompt" },
							{ status: "pending", content: "Run on 50 examples" },
							{ status: "pending", content: "Score variance vs baseline" },
						],
					},
					{
						id: "eval-accuracy",
						status: "pending",
						title: "Eval: factual accuracy",
						meta: { steps: "Steps 0 of 4" },
						summary: ["97% precision", "Reference corpus"],
						items: [
							{ status: "pending", content: "Load reference corpus" },
							{ status: "pending", content: "Cross-check claims" },
							{ status: "pending", content: "Score precision" },
							{ status: "pending", content: "Flag low-confidence rows" },
						],
					},
					{
						id: "eval-latency",
						status: "pending",
						title: "Eval: response latency",
						meta: { steps: "Steps 0 of 2" },
						summary: ["p95 124ms", "100 samples"],
						items: [
							{ status: "pending", content: "Warm pool, time 100 requests" },
							{ status: "pending", content: "Compute p95" },
						],
					},
				],
			},
		},
	],
}

function formatTimestamp(d) {
	const pad = (n) => String(n).padStart(2, "0")
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/**
 * Reset shape: every stage (activity or group) pending, every item pending,
 * meta cleared to a "Steps 0 of N" / no-elapsed look.
 */
function freshActivity(act) {
	return {
		...act,
		status: "pending",
		meta: { steps: `Steps 0 of ${act.items.length}` },
		items: act.items.map((it) => ({ ...it, status: "pending" })),
	}
}
function freshState() {
	return {
		...SEED,
		stages: SEED.stages.map((s) => {
			if (s.activity) return { ...s, activity: freshActivity(s.activity) }
			if (s.group) {
				return {
					...s,
					group: {
						...s.group,
						status: "pending",
						activities: s.group.activities.map(freshActivity),
					},
				}
			}
			return s
		}),
	}
}

/**
 * Build an ordered list of state-mutation events. Each event has an absolute
 * `at` time in ms from demo start, and a function `apply(data) -> data`.
 */
function buildDemoTimeline(initial) {
	const events = []
	let t = 250 // initial settle

	for (const stage of initial.stages) {
		if (stage.activity) {
			t = scheduleActivityStage(events, t, stage)
		} else if (stage.group) {
			t = scheduleGroupStage(events, t, stage)
		}
		t += 560 // pause before the next stage starts
	}

	return events
}

/**
 * Returns ms to wait after stage activation before items start. Any
 * lifecycle badge marked `block: true` contributes the sum of its
 * non-final phase durations. Multiple blocking badges → take the max
 * so items wait for the slowest gate to clear.
 */
function computeBadgeBlockDelay(trigger) {
	if (!trigger) return 0
	const list = Array.isArray(trigger) ? trigger : [trigger]
	let maxDelay = 0
	for (const badge of list) {
		if (!badge.block) continue
		const phases = badge.phases || [badge]
		let total = 0
		for (let i = 0; i < phases.length - 1; i++) {
			total += phases[i].duration || 0
		}
		if (total > maxDelay) maxDelay = total
	}
	return maxDelay
}

function scheduleActivityStage(events, startT, stage) {
	let t = startT
	const total = stage.activity.items.length
	// Captured at schedule time so we can restore the final-shape summary
	// on completion (and append a fresh timestamp for string summaries).
	const originalSummary = stage.activity.summary

	events.push({
		at: t,
		apply: (d) => {
			let next = setStageStatus(d, stage.id, "active")
			if (originalSummary != null) {
				next = setStageSummary(next, stage.id, null)
			}
			return next
		},
	})
	t += 260 // let the card expand / un-dim before items start
	t += computeBadgeBlockDelay(stage.trigger) // hold items until any blocking badge clears

	for (let i = 0; i < total; i++) {
		const item = stage.activity.items[i]
		events.push({ at: t, apply: (d) => setItemStatus(d, stage.id, i, "active") })
		t += 850
		events.push({
			at: t,
			apply: (d) => {
				let next = setItemStatus(d, stage.id, i, "complete")
				next = setStageMeta(next, stage.id, { steps: `Steps ${i + 1} of ${total}` })
				if (item.summaryAt != null) {
					next = setStageSummary(next, stage.id, item.summaryAt)
				}
				return next
			},
		})
		t += 120
	}

	t += 320 // breath after last green check before collapse
	events.push({
		at: t,
		apply: (d) => {
			let next = setStageStatus(d, stage.id, "complete")
			if (originalSummary != null) {
				if (typeof originalSummary === "string") {
					const stamp = formatTimestamp(new Date())
					next = setStageSummary(next, stage.id, `${originalSummary} · Completed ${stamp}`)
				} else {
					next = setStageSummary(next, stage.id, originalSummary)
				}
			}
			return next
		},
	})
	return t
}

/**
 * Parallel group scheduling: all sub-activities go "active" together, each
 * progresses through its own items at its own per-item pace, each collapses
 * independently when its last item finishes. Group completes after the
 * slowest sub-activity collapses. Per-item delays are picked so the subs
 * finish at staggered times rather than landing at the same instant.
 */
function scheduleGroupStage(events, startT, stage) {
	let t = startT

	// Per-sub item duration in ms — pick varied paces so the subs collapse
	// in sequence (you watch them race, then close one by one).
	const perItemMs = [700, 600, 900]

	events.push({ at: t, apply: (d) => setGroupStatus(d, stage.id, "active") })
	t += 260
	t += computeBadgeBlockDelay(stage.trigger) // hold sub-activities until any blocking badge clears

	for (let j = 0; j < stage.group.activities.length; j++) {
		events.push({ at: t, apply: (d) => setSubStatus(d, stage.id, j, "active") })
	}

	const subEndTimes = []
	for (let j = 0; j < stage.group.activities.length; j++) {
		const sub = stage.group.activities[j]
		const subTotal = sub.items.length
		const perItem = perItemMs[j % perItemMs.length]
		let subT = t + 120 // small kick-off offset so items don't fire same instant as expand

		for (let i = 0; i < subTotal; i++) {
			events.push({ at: subT, apply: (d) => setSubItemStatus(d, stage.id, j, i, "active") })
			subT += perItem
			events.push({
				at: subT,
				apply: (d) => {
					let next = setSubItemStatus(d, stage.id, j, i, "complete")
					return setSubMeta(next, stage.id, j, { steps: `Steps ${i + 1} of ${subTotal}` })
				},
			})
			subT += 90
		}
		subT += 280 // breath before sub collapses
		events.push({ at: subT, apply: (d) => setSubStatus(d, stage.id, j, "complete") })
		subEndTimes.push(subT)
	}

	const groupEnd = Math.max(...subEndTimes) + 320
	events.push({ at: groupEnd, apply: (d) => setGroupStatus(d, stage.id, "complete") })
	return groupEnd
}

function Design() {
	const [data, setData] = useState(SEED)
	const [demoRunning, setDemoRunning] = useState(false)
	const timeoutsRef = useRef([])

	const clearScheduled = useCallback(() => {
		timeoutsRef.current.forEach((id) => clearTimeout(id))
		timeoutsRef.current = []
	}, [])

	useEffect(() => clearScheduled, [clearScheduled])

	const runDemo = useCallback(() => {
		clearScheduled()
		const initial = freshState()
		setData(initial)
		setDemoRunning(true)
		const events = buildDemoTimeline(initial)
		events.forEach((ev) => {
			const id = setTimeout(() => {
				setData((prev) => ev.apply(prev))
			}, ev.at)
			timeoutsRef.current.push(id)
		})
		// Tail timeout that flips the running flag off when everything's done.
		const last = events[events.length - 1]
		if (last) {
			const tailId = setTimeout(() => setDemoRunning(false), last.at + 200)
			timeoutsRef.current.push(tailId)
		}
	}, [clearScheduled])

	const reset = useCallback(() => {
		clearScheduled()
		setDemoRunning(false)
		setData(freshState())
	}, [clearScheduled])

	return (
		<Tile className="design-root">
			<div className="design-column">
				<div className="design-toolbar">
					<button
						type="button"
						className="design-pill-button design-pill-button--primary"
						onClick={runDemo}>
						<Play
							size={12}
							strokeWidth={0}
							fill="#ffffff" />
						{demoRunning ? "Restart demo" : "Run demo"}
					</button>
					<button
						type="button"
						className="design-pill-button"
						onClick={reset}>
						Reset
					</button>
				</div>

				<div className="design-stages">
					{(data.stages || []).map((stage, i) => (
						<Stage
							key={stage.id ?? i}
							stage={stage} />
					))}
				</div>
			</div>
		</Tile>
	)
}

export default Design
